import React from 'react';
import PropTypes from 'prop-types';

import { DsTeamIcon } from 'common/react/components/DsTeamIcon';
import {
    StatusBadge,
    SCORE_UPDATE_SHAPE,
    PERIOD_EVENT_SHAPE
} from './StatusBadge';
import { ScoreGroup } from './ScoreGroup';

const { MATCH_STATUS } = PULSE.app.common.CONSTANTS;

/**
 * Desktop match card layout. Props passed straight through from MatchCard.jsx.
 *
 * @param {object} props - Props.
 * @param {object} props.match - The normalised match object from useMatchCardData.
 * @param {string} props.genericStatus - One of PULSE.app.common.CONSTANTS.MATCH_STATUS.
 * @param {boolean} [props.isPlaceholder] - True when either team is a placeholder.
 * @param {string} props.matchId - The match's providerId.
 * @param {object} [props.scoreUpdate] - { show, team, type } from useScoreUpdate.
 * @param {object} [props.periodEvent] - { show, period, phase } from usePeriodEvent.
 * @param {string} [props.fallbackIcon] - Fallback crest modifier for placeholder teams.
 * @returns {React.ReactElement} The Desktop layout.
 */
export const MatchCardDesktop = ({
    match,
    genericStatus,
    matchId,
    scoreUpdate,
    periodEvent,
    fallbackIcon
}) => {
    const showScoreGroup =
        genericStatus === MATCH_STATUS.LIVE ||
        genericStatus === MATCH_STATUS.COMPLETED;

    return (
        <>
            <span className="label-l-semi-bold text-on-surface-variant uppercase">
                {match.homeTeamAbbr}
            </span>
            <DsTeamIcon
                className="shrink-0"
                providerId={match.homeTeamPid}
                theme="notext"
                size="large"
                fallbackIcon={fallbackIcon}
            />
            {showScoreGroup ? (
                <ScoreGroup
                    match={match}
                    genericStatus={genericStatus}
                    matchId={matchId}
                    scoreUpdate={scoreUpdate}
                    periodEvent={periodEvent}
                />
            ) : (
                <StatusBadge
                    match={match}
                    genericStatus={genericStatus}
                    matchId={matchId}
                    scoreUpdate={scoreUpdate}
                    periodEvent={periodEvent}
                />
            )}
            <DsTeamIcon
                className="shrink-0"
                providerId={match.awayTeamPid}
                theme="notext"
                size="large"
                fallbackIcon={fallbackIcon}
            />
            <span className="label-l-semi-bold text-on-surface-variant uppercase">
                {match.awayTeamAbbr}
            </span>
        </>
    );
};

MatchCardDesktop.propTypes = {
    match: PropTypes.object.isRequired,
    genericStatus: PropTypes.string.isRequired,
    isPlaceholder: PropTypes.bool,
    matchId: PropTypes.string.isRequired,
    scoreUpdate: SCORE_UPDATE_SHAPE,
    periodEvent: PERIOD_EVENT_SHAPE,
    fallbackIcon: PropTypes.string
};
